import React, { useEffect, useState } from "react";
import { IonContent, IonButton, IonIcon } from "@ionic/react";
import {
  GuestDashboardImage,
  ReflectionImage,
  CaregiverImage,
  CaregiverImageMobile,
} from "../../../assets/icons";
import { chevronBack, chevronForward } from "ionicons/icons";
import analytics from "../../../services/firebaseAnalytics";
import WellnessCircle from "./WellnessCircle";
import useAuth from "../../../hooks/useAuth";

interface InfoArcConnectProps {
  closeModal: () => void;
  nextStep: () => void;
  prevStep?: () => void;
}

interface PageConfig {
  id: number;
  title: string;
  subtitle: string;
  background: {
    type: "image" | "color" | "gradient" | "transparent" | "";
    value: string;
    opacity?: number;
  };
  overlay?: {
    enabled: boolean;
    color: string;
    opacity: number;
  };
  centerImage?: {
    src: string;
    position: "center" | "top" | "bottom";
    size: "small" | "medium" | "large";
    opacity?: number;
  };
  animatedText?: string;
  textColor: string;
}

export const InfoArcConnect: React.FC<InfoArcConnectProps> = ({ nextStep }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAnimatedText, setShowAnimatedText] = useState(true);

  const { platFormType } = useAuth();
  // Bound the width if we are on web since the modal is constrained to 450px max
  const effectiveWidth =
    platFormType === "web"
      ? Math.min(window.innerWidth, 450)
      : window.innerWidth;
  const isLargeScreen = effectiveWidth >= 768;
  // Configuration array for all pages
  const pages: PageConfig[] = [
    {
      id: 1,
      title: "Welcome to Arc Connect",
      subtitle:
        "Take control of your respiratory wellness with confidence and ease. This app supports you and your caregivers every step of the way.",
      background: {
        type: "image",
        value: isLargeScreen ? CaregiverImage : CaregiverImage,
        opacity: 1,
      },
      animatedText: "Confidence starts with clarity",
      textColor: "black",
    },
    {
      id: 2,
      title: "Your progress, at a glance",
      subtitle:
        "Easily track your therapy goals and progress to stay on course.",
      background: {
        type: "color",
        value: "",
      },
      centerImage: {
        src: GuestDashboardImage,
        position: "center",
        size: "large",
        opacity: 1,
      },
      animatedText: "Small steps add up",
      textColor: "black",
    },
    {
      id: 3,
      title: "Your journey, your reflections",
      subtitle:
        "Reflect on your therapy and share insights with your care team",
      background: {
        type: "",
        value: "",
      },
      centerImage: {
        src: ReflectionImage,
        position: "center",
        size: "large",
        opacity: 1,
      },
      animatedText: "Your story matters in every reflection",
      textColor: "black",
    },
    {
      id: 4,
      title: "Bring it all together",
      subtitle:
        "Arc Connect integrates with other health apps to give you a unified view of your wellness.",
      background: {
        type: "",
        value: "",
      },
      animatedText: "Clarity comes from seeing the whole story",
      textColor: "black",
    },
  ];

  // Animate only the sub text
  useEffect(() => {
    setShowAnimatedText(false);
    const trackInfoScreenView = async () => {
      await analytics.logEvent("onboarding_info_arc_connect_viewed", {
        page_number: currentPage + 1,
      });
    };
    trackInfoScreenView();
    const timeout = setTimeout(() => setShowAnimatedText(true), 800);
    return () => clearTimeout(timeout);
  }, [currentPage]);

  const handleTransition = (newPage: number) => {
    setIsTransitioning(true);
    setShowAnimatedText(false);
    setTimeout(() => {
      setCurrentPage(newPage);
      setShowAnimatedText(true);
      setIsTransitioning(false);
    }, 300);
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      handleTransition(currentPage + 1);
    } else {
      nextStep();
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      handleTransition(currentPage - 1);
    }
  };

  const currentPageConfig = pages[currentPage];

  const getBackgroundStyle = () => {
    const { background } = currentPageConfig;

    switch (background.type) {
      case "image":
        return {
          backgroundImage: `url(${background.value})`,
          backgroundSize: isLargeScreen ? "cover" : "contain",
          backgroundPosition: !isLargeScreen ? "bottom" : "center",
          backgroundRepeat: "no-repeat",
          opacity: background.opacity || 1,

          // scale: "0.8",
          // marginTop: isLargeScreen ? "0" : "20%",
          transform: isLargeScreen ? "scaleX(-1)" : "scaleX(-1)",
          // marginTop: "140px",
          // marginBottom: "100px",
          maskImage: `linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%)`,
          WebkitMaskImage: `linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%)`,
        };
      case "gradient":
        return {
          background: background.value,
          opacity: background.opacity || 1,
        };
      case "color":
        return {
          backgroundColor: background.value,
          opacity: background.opacity || 1,
        };
      case "transparent":
      default:
        return {
          background: "transparent",
        };
    }
  };

  // Inline styles for indicators
  const indicatorModernStyle: React.CSSProperties = {
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    backdropFilter: "blur(5px)",
  };
  const indicatorActiveStyle: React.CSSProperties = {
    background: "#00acdb",
    transform: "scale(1.5)",
    boxShadow: "#00acdb",
  };
  const indicatorInactiveStyle: React.CSSProperties = {
    background: "rgb(178, 213, 223)",
    transform: "scale(1)",
  };
  // Get responsive dimensions based on screen size
  // Get responsive dimensions based on screen size
  // Optimized for iPhone 13 (390px) and iPhone 13 Pro Max (428px) as baseline
  const getResponsiveDimensions = () => {
    const screenWidth = effectiveWidth;
    const screenHeight =
      platFormType === "web" ? window.innerHeight * 0.9 : window.innerHeight;
    // Base dimensions for phone mockup
    let width, height;

    if (screenWidth <= 320) {
      // iPhone SE (3rd gen) and smaller
      width = Math.min(180, screenWidth * 0.55);
      height = width * 2.16; // iPhone aspect ratio
    } else if (screenWidth <= 375) {
      // iPhone 12 mini, iPhone 13 mini
      width = Math.min(210, screenWidth * 0.56);
      height = width * 2.16;
    } else if (screenWidth <= 390) {
      // iPhone 13, iPhone 13 Pro (current baseline)
      width = 250;
      height = 540;
    } else if (screenWidth <= 428) {
      // iPhone 13 Pro Max, iPhone 14 Plus (current baseline)
      width = 240;
      height = 580;
    } else if (screenWidth <= 768) {
      // iPad mini and small tablets
      width = Math.min(300, screenWidth * 0.4);
      height = width * 2.16;
    } else if (screenWidth <= 1024) {
      // iPad and medium tablets
      width = Math.min(350, screenWidth * 0.35);
      height = width * 2.16;
    } else {
      // iPad Pro and larger screens
      width = Math.min(400, screenWidth * 0.3);
      height = width * 2.16;
    }

    // Ensure height doesn't exceed available space
    const maxHeight = screenHeight * 0.65; // 55% of screen height for better proportion
    if (height > maxHeight) {
      height = maxHeight;
      width = height / 2.16;
    }

    return { width: Math.floor(width), height: Math.floor(height) };
  };

  // Get the appropriate arrow vertical position based on current page
  const getArrowPosition = () => {
    if (currentPage === 3) {
      // For page 3 (WellnessCircle), position arrows relative to the circle center
      return "top-[55%]"; // Adjust this value to align with the WellnessCircle center
    }
    // For other pages, use the standard center positioning
    return "top-1/2";
  };

  return (
    <IonContent
      className="ion-padding onboarding-content"
      scrollY={false}
      style={
        {
          "--background": isLargeScreen ? "white" : "#F7F4f2",
          "--color": currentPageConfig.textColor,
          "--offset-top": "0px",
          overflow: "hidden",
          position: "relative",
          minHeight: platFormType === "web" ? "100%" : "100vh",
          height: platFormType === "web" ? "100%" : "100vh",
        } as React.CSSProperties
      }
    >
      {/* Background wrapper */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          ...getBackgroundStyle(),
          zIndex: 0,
          // paddingTop: "20px",
          pointerEvents: "none",
        }}
      />

      {/* Optional overlay */}
      {currentPageConfig.overlay?.enabled && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: currentPageConfig.overlay.color,
            opacity: currentPageConfig.overlay.opacity,
            zIndex: 1,
          }}
        />
      )}

      {/* Main content container with better spacing */}
      <div
        className="flex flex-col h-full w-full relative"
        style={{
          zIndex: 2,
          // paddingTop: "env(safe-area-inset-top, 20px)",
          paddingBottom: "env(safe-area-inset-bottom, 20px)",
        }}
      >
        {/* Top navigation bar with proper spacing */}
        <div className="flex justify-between z-50 items-center w-full px-2 py-2 min-h-[20px] mt-6">
          {/* Back button */}
          <div className="flex-1 flex justify-start">
            {currentPage > 0 ? (
              <button
                className="back-text-button flex items-center gap-1"
                onClick={handlePrevious}
                style={{
                  minWidth: "auto",
                  padding: "8px 12px",
                  color: "#007aff",
                  fontSize: isLargeScreen ? "1.2rem" : "1rem",
                  backgroundColor: "transparent",
                  border: "none",
                }}
              >
                <IonIcon icon={chevronBack} className="back-icon" />
                <span>Back</span>
              </button>
            ) : (
              <div style={{ width: "60px" }} />
            )}
          </div>

          {/* Page indicators - centered */}
          <div className="flex gap-2">
            {pages.map((_, index) => (
              <div
                key={index}
                className="w-3 h-3 rounded-full"
                style={{
                  ...indicatorModernStyle,
                  ...(index === currentPage
                    ? indicatorActiveStyle
                    : indicatorInactiveStyle),
                  transitionDelay: `${index * 0.1}s`,
                }}
              />
            ))}
          </div>

          {/* Skip button */}
          <div className="flex-1 flex justify-end">
            {currentPage < pages.length - 1 ? (
              <button
                className="skip-setup-button"
                onClick={nextStep}
                style={{
                  minWidth: "auto",
                  padding: "8px 12px",
                  color: "#007aff",
                  fontSize: isLargeScreen ? "1.2rem" : "1rem",

                  backgroundColor: "transparent",
                  border: "none",
                }}
              >
                Skip
              </button>
            ) : (
              <div style={{ width: "60px" }} />
            )}
          </div>
        </div>

        {/* Content area with proper vertical distribution */}
        <div
          className={`${
            !isLargeScreen ? "flex-1" : ""
          } flex flex-col justify-center items-center px-4 relative`}
          style={{
            touchAction: "pan-y",
            minHeight: 0, // Allow flex shrinking
          }}
          onTouchStart={(e) => {
            (e.currentTarget as any)._touchStartX = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            const startX = (e.currentTarget as any)._touchStartX;
            const endX = e.changedTouches[0].clientX;
            const diff = endX - startX;
            if (Math.abs(diff) > 50) {
              if (diff < 0 && currentPage < pages.length - 1) {
                handleTransition(currentPage + 1);
              } else if (diff > 0 && currentPage > 0) {
                handleTransition(currentPage - 1);
              }
            }
          }}
        >
          {/* Side navigation arrows with dynamic positioning */}
          {currentPage > 0 && (
            <button
              className={`absolute left-2 ${getArrowPosition()} z-10`}
              style={{
                border: "none",
                backgroundColor: "transparent",
                transform: "translateY(-50%)",
                padding: "1px",
              }}
              onClick={handlePrevious}
              aria-label="Previous"
            >
              <IonIcon
                icon={chevronBack}
                style={{ fontSize: 28, color: "#007aff" }}
              />
            </button>
          )}

          {currentPage < pages.length - 1 && (
            <button
              className={`absolute right-2 ${getArrowPosition()} z-10`}
              style={{
                border: "none",
                backgroundColor: "transparent",
                transform: "translateY(-50%)",
                padding: "1px",
              }}
              onClick={handleNext}
              aria-label="Next"
            >
              <IonIcon
                icon={chevronForward}
                style={{ fontSize: 28, color: "#007aff" }}
              />
            </button>
          )}

          {/* Text content with improved spacing */}
          <div className="flex flex-col items-center text-center w-full max-w-sm mx-auto">
            <h1
              className=" tracking-wide text-medium font-bold"
              style={{
                marginTop: !isLargeScreen ? "4em" : "1em",
                color: currentPageConfig.textColor,
                lineHeight: "1.3",
                fontSize: isLargeScreen ? "22px" : "16px",
              }}
            >
              {currentPageConfig.title}
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{
                color: currentPageConfig.textColor,
                fontSize: isLargeScreen ? "22px" : "15px",
                marginBottom: `${isLargeScreen ? "200px" : ""}`,
                // paddingBottom: "1em",
              }}
            >
              {currentPageConfig.subtitle}
            </p>
          </div>

          {/* Center Image with better proportions */}
          {currentPageConfig.centerImage &&
            currentPage !== 0 &&
            currentPage !== 3 && (
              <div className="flex justify-center mb-3 mt-2">
                <div className="relative ">
                  <div className="relative bg-gray-800 rounded-[1.5rem] p-1 shadow-2xl">
                    <div className="bg-black rounded-[1.5rem] p-1">
                      <div
                        className="bg-white rounded-[1rem] overflow-hidden relative"
                        style={{
                          width: getResponsiveDimensions().width,
                          height: getResponsiveDimensions().height,
                        }}
                      >
                        {/* Notch */}
                        <div
                          className="absolute"
                          style={{
                            top: "0.5rem",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "black",
                            borderRadius: "9999px",
                            width: "3rem",
                            height: "1rem",
                            zIndex: 10,
                          }}
                        />
                        {/* Image content */}
                        <img
                          src={currentPageConfig.centerImage.src}
                          alt="Content illustration"
                          className="w-full h-full object-cover transition-opacity duration-500 ease-in-out"
                          style={{
                            opacity: currentPageConfig.centerImage.opacity || 1,
                          }}
                        />
                      </div>
                    </div>
                    {/* Home indicator */}
                    <div
                      className="absolute"
                      style={{
                        bottom: "0.25rem",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#4b5563",
                        borderRadius: "9999px",
                        width: "6rem",
                        height: "0.15rem",
                      }}
                    />
                  </div>
                  {/* Glow effect */}
                  <div
                    className="absolute inset-0 rounded-[2rem] opacity-20 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at center, rgba(255, 255, 255, 0.4) 0%, transparent 70%)",
                      filter: "blur(24px)",
                      transform: "scale(1.08)",
                    }}
                  />
                </div>
              </div>
            )}
          {currentPage === 0 && (
            <div className="flex justify-center  mt-2">
              <div
                className="relative flex items-center justify-center"
                // style={{ width: "270px", height: "550px" }}
              >
                <div
                  className=" rounded-[1rem] overflow-hidden flex items-center justify-center"
                  style={{
                    width: getResponsiveDimensions().width,
                    height: getResponsiveDimensions().height,
                  }}
                ></div>
              </div>
            </div>
          )}
          {currentPage === 3 && (
            <div className="flex justify-center mb-1 ">
              <div className="">
                <div className="">
                  <div
                    className=" rounded-[1rem] overflow-hidden relative"
                    style={{
                      width: getResponsiveDimensions().width + 150,
                      height: getResponsiveDimensions().height,
                    }}
                  >
                    <WellnessCircle containerWidth={effectiveWidth} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Home indicator */}

          {/* Animated text with proper spacing */}
          <div className="text-center">
            <h2
              className={`font-bold mt-4 mb-2 ${
                effectiveWidth >= 768 ? "mt-8" : ""
              }`}
              style={{
                color: currentPageConfig.textColor,
                fontSize: effectiveWidth >= 768 ? "1.2rem" : "1rem",
                opacity: showAnimatedText ? 1 : 0,
                transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                minHeight: "2rem",
              }}
            >
              {currentPageConfig.animatedText && showAnimatedText && (
                <>{currentPageConfig.animatedText}</>
              )}
            </h2>
          </div>
        </div>

        {/* Bottom button area with safe area padding */}
        <div className="flex justify-center items-center w-full px-4 py-4 mt-4">
          {currentPage === pages.length - 1 && (
            <IonButton
              className="primary-button w-full max-w-xs"
              onClick={handleNext}
              disabled={isTransitioning}
              style={{
                height: "48px",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Get Started
            </IonButton>
          )}
        </div>
      </div>
    </IonContent>
  );
};
